// nurseryMaterial.ts — propagation material, germination tests, mother plants
// and sourcing requests (docs/nursery-propagation-plan.md, Phase 1).

import type { Numeric } from '@/utils/numeric';

export type MaterialKind = 'seed' | 'cutting' | 'budwood' | 'graftwood' | 'explant';
export type MaterialUnit = 'seeds' | 'grams' | 'cuttings' | 'buds' | 'sticks' | 'explants';
export type MaterialSourceType = 'lot' | 'cultivar_area' | 'supplier' | 'other';
export type MaterialStatus = 'open' | 'used' | 'written_off';

export const MATERIAL_KINDS: MaterialKind[] = ['seed', 'cutting', 'budwood', 'graftwood', 'explant'];

export const MATERIAL_KIND_LABELS: Record<MaterialKind, string> = {
  seed: 'Seed', cutting: 'Cuttings', budwood: 'Budwood', graftwood: 'Graftwood', explant: 'Explants',
};

/** The units each kind is counted in; the first is the default. Mirrors the server's UNITS_FOR_KIND. */
export const UNITS_FOR_KIND: Record<MaterialKind, MaterialUnit[]> = {
  seed: ['seeds', 'grams'],
  cutting: ['cuttings'],
  budwood: ['buds', 'sticks'],
  graftwood: ['sticks', 'buds'],
  explant: ['explants'],
};

export const MATERIAL_STATUS_LABELS: Record<MaterialStatus, string> = {
  open: 'On hand', used: 'Used up', written_off: 'Written off',
};

export interface SeedTestSummary {
  tested_on: string;
  seeds_tested: number;
  seeds_germinated: number;
  germination_pct: Numeric;
}

export interface MaterialBundle {
  id: number;
  organisation_id: number;
  kind: MaterialKind;
  cultivar_id: number;
  unit: MaterialUnit;
  quantity: Numeric;
  remaining: Numeric;
  is_estimate: boolean;
  seeds_per_kg: Numeric;
  source_type: MaterialSourceType;
  source_lot_id: number | null;
  source_cultivar_area_id: number | null;
  supplier_id: number | null;
  source_text: string | null;
  collected_on: string;
  collected_by_user_id: number | null;
  collected_by_worker_id: number | null;
  location_id: number | null;
  use_by: string | null;
  stratify_start: string | null;
  stratify_end: string | null;
  certificate_scheme: string | null;
  certificate_number: string | null;
  status: MaterialStatus;
  written_off_reason: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // joined
  cultivar_name: string;
  cultivar_role?: string | null;
  source_lot_code?: string | null;
  source_lot_cultivar_name?: string | null;
  source_cultivar_area_name?: string | null;
  source_farm_name?: string | null;
  supplier_name?: string | null;
  collected_by_name?: string | null;
  location_name?: string | null;
  latest_test?: SeedTestSummary | null;
}

export type MaterialEventType = 'collected' | 'adjust' | 'draw' | 'write_off' | 'reopen';

export interface MaterialEvent {
  id: number;
  material_id: number;
  event: MaterialEventType;
  quantity_delta: Numeric;
  remaining_after: Numeric;
  overrun: Numeric;
  movement_id: number | null;
  reason: string | null;
  recorded_by_user_id: number | null;
  recorded_by_name?: string | null;
  recorded_at: string;
}

export type SeedTestMethod = 'paper_towel' | 'tray' | 'cut' | 'other';

export const SEED_TEST_METHOD_LABELS: Record<SeedTestMethod, string> = {
  paper_towel: 'Paper towel', tray: 'Tray', cut: 'Cut test', other: 'Other',
};

export interface SeedTest {
  id: number;
  material_id: number;
  tested_on: string;
  tested_by_name?: string | null;
  method: SeedTestMethod;
  seeds_tested: number;
  seeds_germinated: number;
  days_to_germinate: Numeric;
  notes: string | null;
  germination_pct: Numeric;
}

export interface MaterialDetail extends MaterialBundle {
  events: MaterialEvent[];
  tests: SeedTest[];
}

export interface MaterialPayload {
  kind: MaterialKind;
  cultivar_id: number;
  unit?: MaterialUnit;
  quantity: number;
  is_estimate?: boolean;
  seeds_per_kg?: number | null;
  source_type: MaterialSourceType;
  source_lot_id?: number | null;
  source_cultivar_area_id?: number | null;
  supplier_id?: number | null;
  source_text?: string | null;
  collected_on: string;
  location_id?: number | null;
  use_by?: string | null;
  propagation_method_id?: number | null;
  stratify_start?: string | null;
  stratify_end?: string | null;
  certificate_scheme?: string | null;
  certificate_number?: string | null;
  notes?: string | null;
  sourcing_request_id?: number | null;
}

export type MaterialUpdatePayload = Partial<Pick<MaterialPayload,
  'use_by' | 'stratify_start' | 'stratify_end' | 'certificate_scheme' | 'certificate_number' | 'notes'
  | 'location_id' | 'seeds_per_kg' | 'is_estimate' | 'source_text'>>;

export interface SeedTestPayload {
  tested_on: string;
  method: SeedTestMethod;
  seeds_tested: number;
  seeds_germinated: number;
  days_to_germinate?: number | null;
  notes?: string | null;
}

/** Where material of a cultivar can come from, in the order to look. */
export interface MaterialSources {
  first: 'mothers' | 'lots' | 'plantings' | null;
  groups: {
    tier: number;
    key: 'mothers' | 'lots' | 'plantings';
    label: string;
    rows: MaterialSourceRow[];
  }[];
}

export interface MaterialSourceRow {
  id: number;
  lot_code?: string | null;
  name?: string | null;
  current_count?: number;
  tree_count?: number | null;
  current_stage?: string;
  tree_type_name?: string | null;
  rootstock_name?: string | null;
  location_name?: string | null;
  farm_name?: string | null;
  block_name?: string | null;
  is_wood_source?: boolean;
}

export interface MotherYield {
  year: number;
  /** A material kind, or 'layers' for rooted layers harvested. */
  what: MaterialKind | 'layers';
  unit: string;
  quantity: number;
  per_plant: number | null;
  last_on: string;
}

export interface MotherPlant {
  kind: 'lot' | 'cultivar_area';
  id: number;
  cultivar_id: number;
  cultivar_name: string;
  rootstock_name: string | null;
  name: string;
  plants: number | null;
  planted_on: string | null;
  where: string | null;
  tree_type_name: string | null;
  yields: MotherYield[];
  last_taken_on: string | null;
}

export type SourcingStatus = 'open' | 'sourced' | 'collected' | 'cancelled';

export interface SourcingRequest {
  id: number;
  organisation_id: number;
  kind: MaterialKind;
  cultivar_id: number;
  cultivar_name: string;
  quantity_wanted: Numeric;
  unit: MaterialUnit | null;
  needed_by: string | null;
  status: SourcingStatus;
  notes: string | null;
  requested_by_name?: string | null;
  requested_at: string;
  source_note: string | null;
  sourced_by_name?: string | null;
  sourced_at: string | null;
  material_id: number | null;
  cancel_reason: string | null;
  /** Answered with the request already open for this cultivar and kind. */
  already_open?: boolean;
}

export interface SourcingRequestPayload {
  kind: MaterialKind;
  cultivar_id: number;
  quantity_wanted?: number | null;
  unit?: MaterialUnit | null;
  needed_by?: string | null;
  notes?: string | null;
}
